/**
 * Weekly rollup hooks.
 *
 *   useWeeklyRollups()                  list (dashboard + PCO L2)
 *   useRollupDetail(pcoId, week)        detail (L3 view + xlsx preview)
 *   downloadRollupXlsx(token, pco, wk)  imperative — fires a download
 *                                       from a button click handler
 */
import { useCallback } from 'react';
import { deriveRollupXlsxFilename } from '@coffman-tm/shared';
import { api } from './api';
import { useApiQuery } from './useApiQuery';
import { useAuth } from './AuthContext';

export function useWeeklyRollups() {
  return useApiQuery((t, signal) => api.rollups.list(t, { signal }));
}

/**
 * PCO cumulative breakdown. Powers the command center page. Aggregates
 * labor / equipment / materials across every week of activity for one PCO.
 *
 * Cache invalidation: this hook refetches on mount, so navigating away to
 * Materials Queue / Settings / etc. and back picks up fresh totals. For
 * intra-page mutations (Mark Complete, Edit PCO details), call refetch()
 * after the mutation succeeds.
 */
export function usePcoCumulative(pcoId: string | null) {
  const fetcher = useCallback(
    (t: string, signal: AbortSignal) => {
      if (!pcoId) {
        return Promise.reject(new Error('usePcoCumulative: pcoId is required'));
      }
      return api.rollups.cumulative(t, pcoId, { signal });
    },
    [pcoId],
  );
  return useApiQuery(fetcher, [pcoId]);
}

/**
 * Regenerated copies of this PCO's contributing dailies — the Sources tab's
 * "Generated PDF" column.
 *
 * Deliberately separate from `usePcoCumulative`: the cumulative call is the
 * money read on every PCO page load, and this one is only worth paying for when
 * the Sources tab is open. Pass `null` to hold it inert, which is what the other
 * three tabs do.
 */
export function usePcoGeneratedDocuments(pcoId: string | null) {
  const fetcher = useCallback(
    (t: string, signal: AbortSignal) => {
      if (!pcoId) {
        return Promise.reject(new Error('usePcoGeneratedDocuments: pcoId is required'));
      }
      return api.rollups.generatedDocuments(t, pcoId, { signal });
    },
    [pcoId],
  );
  return useApiQuery(fetcher, [pcoId]);
}

/**
 * Send history for one week, most-recent first — the Send history panel, and
 * the week page's "sent as original" attribution (each item carries its own
 * per-send `documents` capture).
 *
 * PREFERS THE ROLLUP ID. The (PCO, week) route resolves the week's FIRST
 * rollup, which on a split week is an arbitrary one of two — the same
 * cross-attribution hazard `generatedDocumentsForRollup` exists to close. The
 * week page always has `detail.rollup_id`, so it takes the canonical route;
 * the legacy address stays as the fallback for a week with no persisted rollup
 * row (which has no sends either, so it answers empty).
 */
export function useSubmissions(
  pcoId: string | null,
  weekEndingDate: string | null,
  rollupId: string | null = null,
) {
  const fetcher = useCallback(
    (t: string, signal: AbortSignal) => {
      if (rollupId) return api.rollups.submissionsByRollup(t, rollupId, { signal });
      if (!pcoId || !weekEndingDate) {
        return Promise.reject(new Error('useSubmissions: pcoId and weekEndingDate are required'));
      }
      return api.rollups.submissions(t, pcoId, weekEndingDate, { signal });
    },
    [pcoId, weekEndingDate, rollupId],
  );
  return useApiQuery(fetcher, [pcoId, weekEndingDate, rollupId]);
}

export function useRollupDetail(
  pcoId: string | null,
  weekEndingDate: string | null,
  rollupId?: string | null,
) {
  const fetcher = useCallback(
    (t: string, signal: AbortSignal) => {
      // Canonical addressing wins when a rollup id is supplied — split-week
      // safe (fetches the exact rollup, not the week's sole-or-default).
      if (rollupId) return api.rollups.detailById(t, rollupId, { signal });
      if (!pcoId || !weekEndingDate) {
        return Promise.reject(new Error('useRollupDetail: pcoId and weekEndingDate are required'));
      }
      return api.rollups.detail(t, pcoId, weekEndingDate, { signal });
    },
    [pcoId, weekEndingDate, rollupId],
  );
  // The fetcher above rejects synchronously when params are missing; useApiQuery
  // surfaces that as `error`. Callers should gate the hook usage on having
  // both params (e.g. a route param) or a rollup id, but the explicit reject
  // keeps types consistent.
  return useApiQuery(fetcher, [pcoId, weekEndingDate, rollupId]);
}

/**
 * Imperative xlsx download. Use from an onClick handler — kicks off the
 * fetch, builds a blob URL, programmatically clicks an anchor, then
 * revokes. Returns the filename used so the caller can show
 * "Downloaded as ..." UI.
 *
 * Filename precedence:
 *   1. Caller-provided `filename` (rarely needed; explicit override).
 *   2. The Content-Disposition header from the server — which uses the
 *      shared deriveRollupXlsxFilename helper to match Joey's existing
 *      naming convention.
 *   3. As a last resort, the same helper computed client-side when the
 *      caller can supply pco_number + submission_number. This keeps the
 *      filename correct even if a proxy strips Content-Disposition.
 */
export interface DownloadRollupXlsxHints {
  /** Override the server's filename. Rare; usually leave undefined. */
  filename?: string;
  /** Canonical addressing: when set, downloads the exact rollup (split-week
   *  safe) instead of the week's sole-or-default. */
  rollupId?: string | null;
  /** Fallback inputs if the server's Content-Disposition header is
   *  unavailable. The hook reuses the shared helper to produce a
   *  matching filename. */
  fallback?: {
    pco_number: string;
    submission_number?: string | null;
  };
}

export function useDownloadRollupXlsx() {
  const { token } = useAuth();
  return useCallback(
    async (
      pcoId: string,
      weekEndingDate: string,
      hints: DownloadRollupXlsxHints = {},
    ): Promise<string> => {
      if (!token) throw new Error('Not signed in.');
      const { blob, filename: serverFilename } = hints.rollupId
        ? await api.rollups.downloadXlsxById(token, hints.rollupId)
        : await api.rollups.downloadXlsx(token, pcoId, weekEndingDate);
      const fallback = hints.fallback
        ? deriveRollupXlsxFilename({
            pco_number: hints.fallback.pco_number,
            submission_number: hints.fallback.submission_number ?? null,
            week_ending_date: weekEndingDate,
          })
        : `rollup-${pcoId}-${weekEndingDate}.xlsx`;
      const finalName = hints.filename ?? serverFilename ?? fallback;
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
      return finalName;
    },
    [token],
  );
}
